import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import CoinBadge from "../../components/CoinBadge";
import { X, Plus, Minus, RefreshCw, Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 10;

export default function Monedas() {
    const [students, setStudents] = useState([]);
    const [adjusting, setAdjusting] = useState(null);
    const [confirmingReset, setConfirmingReset] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [page, setPage] = useState(1);

    async function load() {
        const r = await api.get("/users", { params: { role: "alumno" } });
        setStudents(r.data || []);
        setPage(1);
    }
    useEffect(() => { load(); }, []);

    async function doMonthlyReset() {
        setResetting(true);
        try {
            const { data } = await api.post("/coins/monthly-reset");
            toast.success(`✅ ${data.students_updated} alumnos recargados a ${data.new_balance} monedas`);
            setConfirmingReset(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al recargar monedas");
        } finally {
            setResetting(false);
        }
    }

    const totalPages = Math.ceil(students.length / PAGE_SIZE);
    const paginated = students.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="monedas-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-semibold mb-1">Gestión de Monedas</h1>
                    <p className="text-edu-body">
                        Ajusta el saldo de monedas manualmente para cada estudiante. Las solicitudes de recarga
                        de los padres son gestionadas por el ejecutivo.
                    </p>
                </div>
                <button
                    onClick={() => setConfirmingReset(true)}
                    className="edu-btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2 flex-shrink-0"
                    data-testid="monthly-reset-button"
                >
                    <Calendar size={16} /> Recargar mensual (150)
                </button>
            </div>

            <div className="edu-card overflow-hidden">
                <h3 className="text-lg font-semibold p-6 pb-3">Estudiantes</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-edu-soft border-y border-edu">
                            <tr className="text-left">
                                <th className="px-4 py-2">Nombre</th>
                                <th className="px-4 py-2">Grado</th>
                                <th className="px-4 py-2">Saldo</th>
                                <th className="px-4 py-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 && (
                                <tr><td colSpan={4} className="text-center py-6 text-edu-muted">Sin estudiantes.</td></tr>
                            )}
                            {paginated.map((s) => (
                                <tr key={s.id} className="border-b border-edu/50">
                                    <td className="px-4 py-3 font-medium">{s.name}</td>
                                    <td className="px-4 py-3 text-edu-muted">{s.grade}</td>
                                    <td className="px-4 py-3">
                                        <CoinBadge amount={s.coin_balance} size="sm" />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => setAdjusting(s)}
                                            className="px-3 py-1 text-xs rounded-lg bg-edu-navy text-white"
                                            data-testid={`adjust-${s.id}`}
                                        >
                                            Ajustar Monedas
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-edu">
                        <span className="text-xs text-edu-muted">
                            {students.length} estudiantes · Página {page} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronLeft size={16} />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-1.5 rounded-lg border border-edu hover:bg-gray-50 disabled:opacity-40">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {adjusting && (
                <AdjustModal
                    student={adjusting}
                    onClose={() => setAdjusting(null)}
                    onSaved={() => {
                        setAdjusting(null);
                        load();
                    }}
                />
            )}

            {confirmingReset && createPortal(
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={() => setConfirmingReset(false)}>
                    <div className="edu-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xl font-semibold">Recarga mensual</h3>
                            <button onClick={() => setConfirmingReset(false)}><X size={18} /></button>
                        </div>
                        <p className="text-sm text-edu-body mb-2">
                            Esto reseteará el saldo de <strong>todos los alumnos activos ({students.length})</strong> a <strong>150 monedas</strong>, sin importar su saldo actual.
                        </p>
                        <p className="text-xs text-edu-muted mb-5">
                            Esta acción no se puede deshacer automáticamente. Úsala una vez al mes.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setConfirmingReset(false)} className="px-4 py-2 text-sm rounded-xl border border-edu">
                                Cancelar
                            </button>
                            <button
                                onClick={doMonthlyReset}
                                disabled={resetting}
                                className="edu-btn-primary px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {resetting ? <><RefreshCw size={14} className="animate-spin" /> Recargando…</> : "Confirmar recarga"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

function AdjustModal({ student, onClose, onSaved }) {
    const [amount, setAmount] = useState(50);
    const [concept, setConcept] = useState("Recarga manual");
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/coins/transactions/${student.id}`).then((r) => setHistory(r.data || []));
    }, [student.id]);

    async function adjust(sign) {
        setSaving(true);
        try {
            await api.post("/coins/adjust", {
                user_id: student.id,
                amount: sign * Math.abs(amount),
                concept,
            });
            toast.success("Saldo actualizado");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    }

    return createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4" onClick={onClose}>
            <div className="edu-card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto edu-scroll" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">{student.name}</h3>
                    <button onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <p className="text-sm text-edu-body mb-3">
                    Saldo actual: <strong>{student.coin_balance}</strong>
                </p>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(parseInt(e.target.value || "0", 10))}
                        className="edu-input"
                        data-testid="adjust-amount"
                    />
                    <input
                        value={concept}
                        onChange={(e) => setConcept(e.target.value)}
                        className="edu-input"
                        data-testid="adjust-concept"
                    />
                </div>
                <div className="flex gap-3 mb-5">
                    <button
                        onClick={() => adjust(1)}
                        disabled={saving}
                        className="flex-1 edu-btn-accent py-2.5 text-sm inline-flex items-center justify-center gap-1"
                        data-testid="add-coins"
                    >
                        <Plus size={14} /> Agregar
                    </button>
                    <button
                        onClick={() => adjust(-1)}
                        disabled={saving}
                        className="flex-1 bg-red-100 text-red-700 py-2.5 text-sm rounded-xl inline-flex items-center justify-center gap-1"
                        data-testid="remove-coins"
                    >
                        <Minus size={14} /> Quitar
                    </button>
                </div>
                <h4 className="text-sm font-semibold mb-2">Historial</h4>
                <div className="space-y-1 max-h-60 overflow-y-auto edu-scroll">
                    {history.map((t, i) => (
                        <div
                            key={i}
                            className="flex justify-between text-xs p-2 bg-edu-soft rounded-lg"
                        >
                            <span>{t.concept}</span>
                            <span className={t.amount > 0 ? "text-emerald-600" : "text-red-600"}>
                                {t.amount > 0 ? "+" : ""}
                                {t.amount}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
